/**
 * I18nProvider — React Context wrapping the active locale + a
 * setter that persists to cookie + localStorage and, on marketing
 * URLs only, pushes a `?lang=…` query param so the language is
 * shareable. Planet/city routes never invent or overwrite `?lang=`
 * — that query is portalBridge's map-handoff (en|tc|ko|ja|th).
 *
 * Resolution priority on first mount (highest first):
 *   1. URL `?lang=` (shareable; aliases on marketing; `tc`→zh-TW
 *      on planet routes only)
 *   2. cookie `locale`
 *   3. localStorage `jubuddy:i18n:locale:v1`
 *   4. `navigator.languages` first non-ambiguous match
 *   5. 'en' (final fallback; bare `zh` is ambiguous so first paint
 *      is EN, then async detect may pick zh / zh-TW from IP)
 *
 * Language ≠ geography. IP never overrides an English (or any
 * explicit non-zh) browser match. HK + EN system = English UI.
 *
 * Side effects:
 *   - `<html lang="…">` is kept in sync (a11y + screen-reader hint)
 *   - `setLocale(next)` writes cookie + localStorage. On marketing
 *     pages it also `history.replaceState`s `?lang=`. On /planet,
 *     /hk, and city routes it does not touch the query string.
 *   - a non-English locale's dictionary is fetched the first time it
 *     is needed (see LOADERS); English ships in the main chunk.
 *
 * The Provider is wrapped around `<Routes>` in App.tsx so every page
 * has access. /home is the v1 consumer; other routes can opt in by
 * calling `useI18n()` and using the same `t(key)` shape.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  LOCALE_META,
  LOCALE_QUERY_KEY,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  isLocale,
  type Locale,
  type MessageKey,
  type MessageVars,
  type Messages,
} from './types.ts';
import { localeFromCookie, writeLocaleCookie } from './locale-cookie.ts';
import {
  canonicalLocale,
  detectPreferredLocale,
  localeFromQueryParam,
  localesFromBrowser,
  shouldPersistLocaleQuery,
} from './locale-detect.ts';
import en from './dictionaries/en.ts';

/**
 * English ships in the main chunk; every other locale is its own chunk,
 * fetched the first time that locale is needed.
 *
 * All nine used to be static imports, and this module sits in the main
 * entry chunk (App.tsx wraps every route in the Provider), so the whole
 * catalog rode first paint for every visitor — eight-ninths of it in
 * languages that visitor never reads — and every new message key cost
 * main's 250 KB budget (rule 050) nine times over. English stays static:
 * it is the source of truth, the per-key fallback, and what most first
 * paints need.
 *
 * One static import of a dictionary pins it straight back into main —
 * dictionaries.boundary.test.ts holds that line.
 */
const LOADERS: Record<Exclude<Locale, 'en'>, () => Promise<{ default: Messages }>> = {
  zh: () => import('./dictionaries/zh.ts'),
  'zh-TW': () => import('./dictionaries/zh-TW.ts'),
  ja: () => import('./dictionaries/ja.ts'),
  ko: () => import('./dictionaries/ko.ts'),
  th: () => import('./dictionaries/th.ts'),
  nl: () => import('./dictionaries/nl.ts'),
  es: () => import('./dictionaries/es.ts'),
  fr: () => import('./dictionaries/fr.ts'),
};

/** Dictionaries ready to render. English is always here; the rest arrive via LOADERS. */
const loaded = new Map<Locale, Messages>([['en', en]]);
/** One shared request per locale while it is in flight. */
const inFlight = new Map<Locale, Promise<Messages>>();

/**
 * Fetch (once) and cache a locale's dictionary. Concurrent callers share
 * one request; a failed request is forgotten so the next call can retry.
 *
 * The Provider calls this itself. It is exported for synchronous renders:
 * SSR tests await it so a locale is ready before `renderToStaticMarkup`.
 */
export function loadDictionary(locale: Locale): Promise<Messages> {
  const ready = loaded.get(locale);
  if (ready) return Promise.resolve(ready);
  const pending = inFlight.get(locale);
  if (pending) return pending;
  // Partial view: an unknown locale (reachable only through a bug upstream —
  // state is typed Locale) rejects like a failed fetch instead of throwing
  // inside the Provider's effect. The old synchronous lookup could not throw.
  const load = (LOADERS as Partial<Record<Locale, () => Promise<{ default: Messages }>>>)[locale];
  if (!load) return Promise.reject(new Error(`[i18n] no dictionary for locale '${locale}'`));
  const request = load()
    .then((mod) => {
      loaded.set(locale, mod.default);
      return mod.default;
    })
    .finally(() => {
      inFlight.delete(locale);
    });
  inFlight.set(locale, request);
  return request;
}

/** Test-only: forget every dictionary except English, as a fresh page load would. */
export function __resetDictionariesForTests(): void {
  loaded.clear();
  loaded.set('en', en);
  inFlight.clear();
}

/**
 * Single-pass `{name}` substitution — the "single-template-string
 * interpolation handled by t() itself" the types.ts header promises.
 * Unknown slots are left verbatim so a template/vars mismatch is
 * visible in the UI instead of silently vanishing.
 */
function interpolate(template: string, vars?: MessageVars): string {
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (token, name: string) => {
    const value = vars[name];
    return value === undefined ? token : String(value);
  });
}

interface I18nContextValue {
  /** The chosen locale — updates the moment it is chosen, even while
   *  its dictionary is still loading. */
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
  /**
   * Look up a translated message in the dictionary on screen: the
   * chosen locale's, or — while that one is still loading — the last
   * one shown (English on a first paint). Falls back to English when
   * the key is missing from it (defensive — typecheck should already
   * prevent this, but production runtime is paranoid).
   * Optional `vars` fill `{name}` slots in the template.
   */
  readonly t: (key: MessageKey, vars?: MessageVars) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Three small pure helpers — each takes its source as an explicit
 * argument so tests don't need to mock globals. The chained version
 * below reads from `window` at call time.
 */
export function localeFromUrlSearch(
  search: string | undefined | null,
  pathname?: string | null,
): Locale | null {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search);
    return localeFromQueryParam(params.get(LOCALE_QUERY_KEY), pathname);
  } catch {
    return null;
  }
}

export function localeFromStorage(stored: string | undefined | null): Locale | null {
  return canonicalLocale(stored);
}

/**
 * Single BCP-47 tag → site locale. Bare `zh` is AMBIGUOUS and returns
 * null (IP may later pick zh vs zh-TW). Prefer `localesFromBrowser`
 * for the full `navigator.languages` list.
 */
export function localeFromNavigatorLanguage(lang: string | undefined | null): Locale | null {
  if (!lang) return null;
  return localesFromBrowser([lang]).match;
}

/**
 * Pure resolver used by `resolveInitialLocale` and by tests so the
 * priority list (url → cookie → storage → browser → en) is asserted
 * without mocking `window`.
 */
export function resolveLocaleFromSources(input: {
  readonly search?: string | null;
  readonly pathname?: string | null;
  readonly cookieHeader?: string | null;
  readonly stored?: string | null;
  readonly browserLanguages?: readonly string[] | null;
}): Locale {
  const fromUrl = localeFromUrlSearch(input.search, input.pathname);
  if (fromUrl) return fromUrl;
  const fromCookie = localeFromCookie(input.cookieHeader);
  if (fromCookie) return fromCookie;
  const fromStorage = localeFromStorage(input.stored);
  if (fromStorage) return fromStorage;
  const { match } = localesFromBrowser(input.browserLanguages ?? []);
  if (match) return match;
  return 'en';
}

/**
 * Detect the initial locale on first mount. SSR-safe (`typeof window`
 * guard). First paint never waits on IP — bare `zh` or no match is `en`,
 * then the mount effect may call `detectPreferredLocale`.
 */
export function resolveInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';

  try {
    let stored: string | null = null;
    try {
      stored = window.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? null;
    } catch {
      /* localStorage blocked */
    }
    const languages =
      window.navigator?.languages?.length
        ? Array.from(window.navigator.languages)
        : window.navigator?.language
          ? [window.navigator.language]
          : [];
    return resolveLocaleFromSources({
      search: window.location?.search,
      pathname: window.location?.pathname,
      cookieHeader: typeof document !== 'undefined' ? document.cookie : null,
      stored,
      browserLanguages: languages,
    });
  } catch {
    return 'en';
  }
}

function persistPreference(next: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(LOCALE_STORAGE_KEY, next);
  } catch {
    /* storage blocked */
  }
  writeLocaleCookie(next);
}

function syncLocaleQuery(next: Locale): void {
  if (typeof window === 'undefined') return;
  if (!shouldPersistLocaleQuery(window.location.pathname, window.location.search)) {
    return;
  }
  try {
    const url = new URL(window.location.href);
    if (next === 'en') {
      url.searchParams.delete(LOCALE_QUERY_KEY);
    } else {
      url.searchParams.set(LOCALE_QUERY_KEY, next);
    }
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* malformed URL */
  }
}

function readPersistedChoice(): { url: Locale | null; cookie: Locale | null; storage: Locale | null } {
  if (typeof window === 'undefined') {
    return { url: null, cookie: null, storage: null };
  }
  let url: Locale | null = null;
  try {
    url = localeFromUrlSearch(window.location?.search, window.location?.pathname);
  } catch {
    /* malformed URL */
  }
  const cookie = typeof document !== 'undefined' ? localeFromCookie(document.cookie) : null;
  let storage: Locale | null = null;
  try {
    storage = localeFromStorage(window.localStorage?.getItem(LOCALE_STORAGE_KEY));
  } catch {
    /* blocked */
  }
  return { url, cookie, storage };
}

interface I18nProviderProps {
  readonly children: ReactNode;
  /** Optional override for tests; bypasses the resolver. */
  readonly initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? resolveInitialLocale());

  // Keep <html lang="…"> in sync. A11y + screen-reader hint.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  // Persist + sync URL when locale changes by an explicit setter call
  // (NOT on mount — the initial value is already what URL/cookie/storage said).
  const setLocale = useCallback((next: Locale) => {
    if (!isLocale(next)) return;
    setLocaleState(next);
    persistPreference(next);
    syncLocaleQuery(next);
  }, []);

  // After mount: persist a winning URL lang, or run IP/browser detect
  // once when url + cookie + storage are all empty. Tests that pass
  // `initialLocale` skip this so SSR markup stays deterministic.
  useEffect(() => {
    if (initialLocale !== undefined) return;
    if (typeof window === 'undefined') return;
    const persisted = readPersistedChoice();
    if (persisted.url) {
      persistPreference(persisted.url);
      return;
    }
    if (persisted.cookie || persisted.storage) return;
    let cancelled = false;
    void detectPreferredLocale().then((detected) => {
      if (cancelled) return;
      // User switched while we were in flight — honour the cookie.
      if (localeFromCookie(typeof document !== 'undefined' ? document.cookie : null)) return;
      setLocaleState((current) => {
        if (detected === current) return current;
        persistPreference(detected);
        syncLocaleQuery(detected);
        return detected;
      });
    });
    return () => {
      cancelled = true;
    };
    // Run once on mount.
  }, [initialLocale]);

  // The last locale whose dictionary reached the screen. It only matters
  // while `locale`'s own dictionary is in flight: a first paint shows
  // English, and a switch keeps the previous language up — no flash of
  // English — until the new one lands.
  const [shown, setShown] = useState<Locale>(() => (loaded.has(locale) ? locale : 'en'));
  const active: Locale = loaded.has(locale) ? locale : shown;

  // Fetch the requested locale's dictionary unless it is bundled or cached.
  useEffect(() => {
    if (loaded.has(locale)) {
      setShown(locale);
      return;
    }
    let cancelled = false;
    loadDictionary(locale).then(
      () => {
        if (!cancelled) setShown(locale);
      },
      (err: unknown) => {
        // Only the effect still in charge reports it: one failure shared by
        // several abandoned switches is logged once, not once per switch.
        if (!cancelled) {
          console.warn(`[i18n] could not load the '${locale}' dictionary — keeping the language on screen`, err);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const t = useCallback<I18nContextValue['t']>(
    (key, vars) => {
      const dict = loaded.get(active) ?? en;
      // Defensive fallback to English; should be unreachable under
      // typecheck because every dictionary satisfies Messages.
      const value = dict[key] ?? en[key];
      return interpolate(value, vars);
    },
    [active],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook for accessing the active locale + translator function. Throws
 * a helpful error if called outside an `<I18nProvider>` so the bug
 * surfaces immediately at dev time.
 */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useI18n() called outside <I18nProvider>. Wrap your component tree.');
  }
  return ctx;
}

/** Re-exports for consumers that just need the metadata. */
export { LOCALE_META, SUPPORTED_LOCALES };
export type { Locale, MessageKey };
